import expandVttUploadPath from './expandVttUploadPath';

/**
 * Helper function to construct VTT file paths with locale suffixes
 * Checks for human-translated files first, then falls back to AI-translated files
 * @param basePath - The base VTT file path (e.g., "captions/video.vtt")
 * @param locale - The target locale (e.g., "de-DE", "fr-FR")
 * @param options - Optional configuration
 * @returns Promise that resolves to the VTT path with locale suffix if applicable
 */
export default async function findVttPathForLocale(
  basePath: string | null | undefined,
  locale: string,
  options: {
    /** Whether to use regex replacement (default: false, uses simple replace) */
    useRegex?: boolean;
  } = {}
): Promise<string | null | undefined> {
  if (!basePath) return basePath;

  const { useRegex = false } = options;

  // If locale is en-GB, return the original path
  if (locale === 'en-GB') {
    return basePath;
  }

  // First, try to find human-translated version
  const humanTranslatedPath = basePath.replace(
    useRegex ? /\.vtt$/ : '.vtt',
    `_${locale}.human.vtt`
  );

  try {
    // Check if human-translated file exists
    const expandedUrl = expandVttUploadPath(humanTranslatedPath);
    const response = await fetch(expandedUrl, { method: 'HEAD' });
    if (response.ok) {
      // Check if the response is actually a VTT file by examining content-type
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/vtt')) {
        return humanTranslatedPath;
      }
      // Also check for text/plain as some servers serve VTT as plain text
      if (
        contentType &&
        (contentType.includes('text/plain') ||
          contentType.includes('application/octet-stream'))
      ) {
        // we need to check the actual content to be sure
        const contentResponse = await fetch(expandedUrl);
        if (contentResponse.ok) {
          const content = await contentResponse.text();
          // Check if it looks like a VTT file (starts with WEBVTT or has VTT cues)
          if (content.trim().startsWith('WEBVTT') || content.includes('-->')) {
            return humanTranslatedPath;
          }
        }
      }
    }
  } catch (error) {
    // File doesn't exist or network error, continue to fallback
  }

  // Fallback to AI-translated version
  return basePath.replace(useRegex ? /\.vtt$/ : '.vtt', `_${locale}.vtt`);
}
